import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, XCircle, Crown, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

export const PaymentSuccess: React.FC = () => {
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Support both Omise return URL params and legacy Ksher params
  const orderNo = searchParams.get('order_no') || searchParams.get('mch_order_no');
  const { checkAuth } = useAuthStore();
  
  const [status, setStatus] = useState<'loading' | 'success' | 'failed'>('loading');
  const [paymentData, setPaymentData] = useState<any>(null);

  useEffect(() => {
    if (!orderNo) {
      setStatus('failed');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/payments/check-status/${orderNo}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setPaymentData(data.payment);
          
          if (data.payment.status === 'success' || data.payment.status === 'completed') {
            setStatus('success');
            // Refresh user data to update subscription
            await checkAuth();
          } else if (data.payment.status === 'failed') {
            setStatus('failed');
          } else {
            // Still pending, check again after a delay
            setTimeout(checkPaymentStatus, 2000);
          }
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Check payment error:', error);
        setStatus('failed');
      }
    };

    checkPaymentStatus();
  }, [orderNo, checkAuth]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="text-center">
          <Loader className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('success.verifyingTitle')}</h2>
          <p className="text-gray-600">{t('success.verifyingHint')}</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-12 h-12 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('success.failedTitle')}</h1>
          <p className="text-gray-600 mb-8">
            {t('success.failedHint')}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/upgrade')}
              className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all"
            >
              {t('success.tryAgain')}
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-3 px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-all"
            >
              {t('success.backToDashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        {/* Success Animation */}
        <div className="relative mb-6">
          <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto animate-pulse">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          <div className="absolute -top-2 -right-2 w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
            <Crown className="w-8 h-8 text-yellow-800" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('success.successTitle')}
        </h1>
        <p className="text-lg text-gray-600 mb-6">
          <Trans i18nKey="success.welcome" t={t}>
            Welcome to <span className="font-bold text-blue-600">Pro Plan</span> 🎉
          </Trans>
        </p>

        {/* Payment Details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('success.details.orderNo')}</span>
              <span className="font-mono font-semibold text-gray-900 text-xs">
                {paymentData?.orderNo}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('success.details.amount')}</span>
              <span className="font-semibold text-gray-900">
                {paymentData?.currency} {paymentData?.amount}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('success.details.status')}</span>
              <span className="font-semibold text-green-600">
                {t('success.details.paid')}
              </span>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 mb-6">
          <p className="text-sm font-semibold text-gray-900 mb-2">
            {t('success.benefitsTitle')}
          </p>
          <ul className="text-sm text-gray-700 space-y-1 text-left">
            <li>{t('success.benefits.unlimitedPages')}</li>
            <li>{t('success.benefits.allProComponents')}</li>
            <li>{t('success.benefits.advancedAnalytics')}</li>
            <li>{t('success.benefits.botCloaking')}</li>
            <li>{t('success.benefits.prioritySupport')}</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
          >
            {t('success.goToDashboard')}
            <ArrowRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => navigate('/builder')}
            className="w-full py-3 px-6 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-semibold transition-all"
          >
            {t('success.createPage')}
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-6">
          {t('success.receiptNotice')}
        </p>
      </div>
    </div>
  );
};

